import { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { AppBundle } from '../../utils/app-bundles';
import { useLocation } from 'react-router-dom';
import { useActiveBundle } from '../../context/active-bundle';

const MAX_HISTORY_LENGTH = 10;

export interface NavigationContextHistoryState {
  url: string;
  app: AppBundle | null;
}

interface INavigationHistory {
  history: NavigationContextHistoryState[];
}

const NavigationHistory = createContext<INavigationHistory | null>(null);

export function useNavigationHistory() {
  const context = useContext(NavigationHistory);
  if (context == null) {
    throw new Error('useNavigationHistory must be used within a NavigationHistoryProvider');
  }
  return context;
}

export function NavigationHistoryProvider({ children }: { children: ReactNode }) {
  const [history, setHistory] = useState<NavigationContextHistoryState[]>([]);
  const location = useLocation();

  const activeApp = useActiveBundle();
  const activeAppRef = useRef(activeApp);
  useEffect(() => {
    activeAppRef.current = activeApp;
  }, [activeApp]);

  useEffect(() => {
    setHistory((previousHistory) => {
      const lastHistoryItem = previousHistory[previousHistory.length - 1];
      const newHistoryItem = {
        url: `${location.pathname}${location.search}`,
        app: activeAppRef.current ?? null,
      };

      if (lastHistoryItem?.url === newHistoryItem.url) {
        return previousHistory;
      }

      const newHistory = [...previousHistory, newHistoryItem];

      if (newHistory.length > MAX_HISTORY_LENGTH) {
        newHistory.shift();
      }

      return newHistory;
    });
  }, [location]);

  return <NavigationHistory.Provider value={{ history }}>{children}</NavigationHistory.Provider>;
}
